import { useState } from 'react';
import { Eye, Brain, Heart, Sparkles, Zap, Target, BookOpen, ArrowLeft, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CrystalIdentificationGame } from './CrystalIdentificationGame';
import { CrystalPropertiesGame } from './CrystalPropertiesGame';
import { CrystalRemedyMatcher } from './CrystalRemedyMatcher';
import { CrystalFlashCards } from './CrystalFlashCards';
import { CrystalLearningGame } from './CrystalLearningGame';

type GameMode = 'menu' | 'identify' | 'properties' | 'remedies' | 'flashcards' | 'advanced';

interface GameOption {
  id: GameMode;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  xpRange: string;
}

interface CrystalLearningHubProps {
  onExit?: () => void;
}

const gameOptions: GameOption[] = [
  {
    id: 'identify',
    title: 'Name That Crystal',
    description: 'Learn to recognise crystals by their appearance',
    icon: Eye,
    difficulty: 'Beginner',
    xpRange: '10-15 XP'
  },
  {
    id: 'properties',
    title: 'Crystal Properties',
    description: 'Match crystals to their healing properties',
    icon: Sparkles,
    difficulty: 'Beginner',
    xpRange: '10-15 XP'
  },
  {
    id: 'remedies',
    title: 'Crystal Remedies',
    description: 'Learn which crystals help with specific issues',
    icon: Heart,
    difficulty: 'Intermediate',
    xpRange: '15-20 XP'
  },
  {
    id: 'flashcards',
    title: 'Crystal Flash Cards',
    description: 'Study mode with quick facts and tips',
    icon: BookOpen,
    difficulty: 'Beginner',
    xpRange: '5 XP each'
  },
  {
    id: 'advanced',
    title: 'Crystal Master Quiz',
    description: 'Mixed questions on uses, chakras & psychology',
    icon: Brain,
    difficulty: 'Advanced',
    xpRange: '15-25 XP'
  }
];

export function CrystalLearningHub({ onExit }: CrystalLearningHubProps) {
  const [activeGame, setActiveGame] = useState<GameMode>('menu');

  const handleBack = () => {
    setActiveGame('menu');
  };

  const renderGame = () => {
    switch (activeGame) {
      case 'identify':
        return <CrystalIdentificationGame onBack={handleBack} />;
      case 'properties':
        return <CrystalPropertiesGame onBack={handleBack} />;
      case 'remedies':
        return <CrystalRemedyMatcher onBack={handleBack} />;
      case 'flashcards':
        return <CrystalFlashCards onBack={handleBack} />;
      case 'advanced':
        return <CrystalLearningGame />;
      default:
        return null;
    }
  };

  if (activeGame !== 'menu') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setActiveGame('menu')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Learning Hub
          </Button>
          {onExit && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onExit}
            >
              <X className="h-4 w-4 mr-2" />
              Exit
            </Button>
          )}
        </div>
        {renderGame()}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Exit Button */}
      {onExit && (
        <div className="flex justify-end">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onExit}
          >
            <X className="h-4 w-4 mr-2" />
            Exit Learning
          </Button>
        </div>
      )}

      {/* Header */}
      <Card className="bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 border-primary/20">
        <CardContent className="p-6 text-center">
          <div className="p-3 rounded-full bg-primary/20 w-fit mx-auto mb-4">
            <Target className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Crystal Learning Journey
          </h2>
          <p className="text-muted-foreground text-sm">
            Master crystal knowledge through fun, interactive games. Start with basics and progress to advanced healing applications.
          </p>
        </CardContent>
      </Card>

      {/* Learning Path */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground px-1">
          Choose Your Learning Path
        </h3>
        
        {gameOptions.map((game, index) => {
          const Icon = game.icon;
          return (
            <Card 
              key={game.id}
              className="bg-gradient-to-r from-primary/5 to-accent/5 border-border/50 hover:border-primary/50 transition-all cursor-pointer group"
              onClick={() => setActiveGame(game.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-background/80 group-hover:bg-primary/10 transition-colors">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
                          {game.title}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {game.description}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            game.difficulty === 'Beginner' ? 'border-accent/50 text-accent' :
                            game.difficulty === 'Intermediate' ? 'border-secondary/50 text-secondary' :
                            'border-destructive/50 text-destructive'
                          }`}
                        >
                          {game.difficulty}
                        </Badge>
                        <span className="text-xs text-primary font-medium">
                          {game.xpRange}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tips */}
      <Card className="bg-muted/30 border-muted">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-500" />
            Learning Tips
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• Start with <strong>Name That Crystal</strong> to learn recognition</p>
          <p>• Then explore <strong>Properties</strong> to understand what each crystal does</p>
          <p>• Practice <strong>Remedies</strong> to know which crystal to use when</p>
          <p>• Use <strong>Flash Cards</strong> anytime for quick revision</p>
          <p>• Streak bonuses: 2+ correct = +3 XP, 3+ = +5 XP!</p>
        </CardContent>
      </Card>
    </div>
  );
}
